import type { BatchTargetList } from "@models/mappingConfig";
import type {
  BatchJobSummaryResponse,
  BatchJobSummaryViewModel,
} from "@models/monitoring";
import { buildBatchJobSummaryViewModel } from "@utils/builders/monitoring";
import { HttpClient } from "@utils/index";

const BASE_URL = import.meta.env.VITE_MAPPING_API_BASE_URL;

const MISSING_BASE_URL_MESSAGE =
  "Missing required environment variable: VITE_MAPPING_API_BASE_URL. " +
  "Please check your .env file and ensure the Mapping API URL is set.";

class MappingApi {
  private httpClient: HttpClient | null;

  constructor() {
    // Defer the missing-config failure to the first call rather than throwing
    // at module load. mappingApi is imported transitively by useMonitoring
    // (and thus the whole admin micro-frontend); a module-scope throw would
    // white-screen every page. This way a missing URL degrades only the
    // Mapping monitoring feature, surfaced via useMonitoring's error state.
    this.httpClient = BASE_URL ? new HttpClient(`${BASE_URL}`) : null;
  }

  /**
   * Enrich a list of mapping job ids with their metadata.
   *
   * Backend contract (POST /jobs/batch): ids that do not exist or belong to
   * another organisation are silently omitted, so the response may be shorter
   * than the request and callers must match on `jobId` (not index alignment).
   */
  public async getJobsBatch(
    jobIds: string[],
  ): Promise<BatchJobSummaryViewModel[]> {
    if (!this.httpClient) {
      throw new Error(MISSING_BASE_URL_MESSAGE);
    }

    const endpoint = `/jobs/batch`;

    // BatchJobResponse wraps the list: { jobs: [...] } — not a bare array.
    const response = await this.httpClient.makeAuthenticatedRequest<{
      jobs: BatchJobSummaryResponse[];
    }>(endpoint, {
      method: "POST",
      body: JSON.stringify({
        job_ids: jobIds,
      }),
    });

    return (response.jobs ?? []).map(buildBatchJobSummaryViewModel);
  }

  /**
   * Services that accept an extraction batch, each with its active plugins.
   * The doctype form offers only these; the mapping trigger validates the
   * same list again when extraction sends a batch.
   */
  public async getBatchTargets(signal?: AbortSignal): Promise<BatchTargetList> {
    if (!this.httpClient) {
      throw new Error(MISSING_BASE_URL_MESSAGE);
    }
    return this.httpClient.makeAuthenticatedRequest<BatchTargetList>(
      "/plugins/batch-targets",
      { method: "GET", signal },
    );
  }
}

export { MappingApi };

export const mappingApi = new MappingApi();
